import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

export const Tooltips = ({ position, content, command, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return { top: 0, left: 0 };

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + 8;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - 8;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + 15;
        break;
      default:
        break;
    }

    // Adjust for window boundaries
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Horizontal boundary check
    if (left < 0) left = 8;
    if (left + tooltipRect.width > windowWidth) {
      left = windowWidth - tooltipRect.width - 8;
    }

    // Vertical boundary check
    if (top < 0) top = 8;
    if (top + tooltipRect.height > windowHeight) {
      top = windowHeight - tooltipRect.height - 8;
    }

    return {
      top: top + window.scrollY,
      left: left + window.scrollX,
    };
  };

  const updatePosition = () => {
    if (isHovered && triggerRef.current && tooltipRef.current) {
      setTooltipPosition(calculatePosition());
    }
  };

  useEffect(() => {
    if (isHovered) {
      const handleUpdate = () => {
        updatePosition();
      };

      handleUpdate();
      window.addEventListener("scroll", handleUpdate);
      window.addEventListener("resize", handleUpdate);

      return () => {
        window.removeEventListener("scroll", handleUpdate);
        window.removeEventListener("resize", handleUpdate);
      };
    }
  }, [isHovered]);

  const tooltipClasses = [
    "fixed", // Use fixed positioning to break out of any stacking context
    "z-[99999]", // Highest possible z-index
    "pointer-events-none", // Prevent tooltip from intercepting mouse events
    "custom-bg-white-50", // Background color
    "custom-shadow", // Subtle shadow
    "text-xs", // Small text
    "p-[8px]", // Vertical padding
    "whitespace-nowrap", // Prevent text wrapping
    "rounded", // Rounded corners
    "custom-text-grey-800", // Text color
    `${(position || position !== '' ) ? 'lg:block hidden': 'hidden'}`
  ].join(" ");

  const renderTooltip = () => {
    if (!isHovered) return null;
    if(!position || position === '') return null;
    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        className={tooltipClasses}
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
        }}
      >
        <div className="grid items-center">
          {content}
          {command && (
            <p className="custom-text-grey-200 text-[12px] poppins-light">
              {command}
            </p>
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={triggerRef}
      className="relative "
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {renderTooltip()}
    </div>
  );
};

Tooltips.propTypes = {
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  content: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  command: PropTypes.string,
};

Tooltips.defaultProps = {
  command: "",
};

export default Tooltips;
